"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.intersectArcArc = intersectArcArc;
exports.intersectArcLine = intersectArcLine;
exports.intersectEntities = intersectEntities;
exports.intersectLineLine = intersectLineLine;
var _vector = require("./vector");
var _checkAngles = require("./checkAngles");
/**
 * Finds any points of intersection between the two entities
 * @param e1 The first entity
 * @param e2 The second entity
 * @param refine For arcs, refine the result using Newton's method
 * @returns An array of intersection points, or an empty array if no intersections were found
 */
function intersectEntities(e1, e2, refine = false) {
  // Convert circles to arcs
  if (e1.type === 'CIRCLE') {
    e1 = {
      ...e1,
      type: 'ARC',
      startAngle: 0,
      endAngle: 2 * Math.PI
    };
  }
  if (e2.type === 'CIRCLE') {
    e2 = {
      ...e2,
      type: 'ARC',
      startAngle: 0,
      endAngle: 2 * Math.PI
    };
  }
  if (e1.type === 'LINE' && e2.type === 'LINE') {
    return intersectLineLine(e1, e2);
  } else if (e1.type === 'ARC' && e2.type === 'LINE') {
    return intersectArcLine(e1, e2).map(({
      point,
      tArc,
      tLine
    }) => ({
      point,
      t1: tArc,
      t2: tLine
    }));
  } else if (e1.type === 'LINE' && e2.type === 'ARC') {
    return intersectArcLine(e2, e1).map(({
      point,
      tArc,
      tLine
    }) => ({
      point,
      t1: tLine,
      t2: tArc
    }));
  } else if (e1.type === 'ARC' && e2.type === 'ARC') {
    return intersectArcArc(e1, e2, refine);
  } else {
    throw new Error(`Unsupported entity types in intersectEntities: ${e1.type} and ${e2.type}`);
  }
}

/**
 * Finds the intersection point between two line segments.
 * See http://www.cs.swan.ac.uk/~cssimon/line_intersection.html
 * @param e1 The first line segment
 * @param e2 The second line segment
 * @returns An array containing a single point of intersection, or an empty array if the line segments do not intersect.
 */
function intersectLineLine(e1, e2) {
  const x1 = e1.start.x;
  const x2 = e1.end.x;
  const x3 = e2.start.x;
  const x4 = e2.end.x;
  const y1 = e1.start.y;
  const y2 = e1.end.y;
  const y3 = e2.start.y;
  const y4 = e2.end.y;
  const den = (x4 - x3) * (y1 - y2) - (x1 - x2) * (y4 - y3);

  // First check bounding boxes of line segments
  if (x1 < x3 && x1 < x4 && x2 < x3 && x2 < x4 || x1 > x3 && x1 > x4 && x2 > x3 && x2 > x4 || y1 < y3 && y1 < y4 && y2 < y3 && y2 < y4 || y1 > y3 && y1 > y4 && y2 > y3 && y2 > y4) {
    return [];
  }
  const taNum = (y3 - y4) * (x1 - x3) + (x4 - x3) * (y1 - y3);
  const tbNum = (y1 - y2) * (x1 - x3) + (x2 - x1) * (y1 - y3);
  const ta = taNum / den;
  const tb = tbNum / den;
  if (0 <= ta && ta <= 1 && 0 <= tb && tb <= 1) {
    return [{
      point: {
        x: x1 + (x2 - x1) * ta,
        y: y1 + (y2 - y1) * ta
      },
      t1: ta,
      t2: tb
    }];
  } else {
    return [];
  }
}

/**
 * Finds the intersection point(s) between an arc and a line.
 * @param arc The arc
 * @param line The line
 * @returns An array containing zero, one, or two points of intersection.
 */
function intersectArcLine(arc, line) {
  // Equation of line segment is p(t) = p1 + t * (p2 - p1)
  // Equation of the circle the arc came from is (x-q.x)^2 + (y-q.y)^2 = r^2
  // Find t that satisfies the circle equation (it will be a quadratic)

  const p1 = line.start;
  const p2 = line.end;
  const q = {
    x: arc.x,
    y: arc.y
  };
  const r = arc.r;

  // Circle equation becomes:
  // (p1.x - q.x + t * (p2.x - p1.x)) ^ 2 + (p1.y -q.y + t * (p2.y - p1.y)) ^ 2 = r^2

  // Then:
  // (A + t * B) ^ 2 + (C + t * D) ^ 2 = r ^ 2 
  // (A^2 + C^2) + (2*A*B + 2*C*D)*t + (B^2+D^2)*t^2 = r^2

  const A = p1.x - q.x;
  const B = p2.x - p1.x;
  const C = p1.y - q.y;
  const D = p2.y - p1.y;
  const a = B * B + D * D;
  const b = 2 * A * B + 2 * C * D;
  const c = A * A + C * C - r * r;

  // Then t are the solutions to the equation
  // a*t^2 + b*t + c = 0

  const disc = b * b - 4 * a * c;
  if (disc < 0) {
    return [];
  }
  let candidateTs;
  if (Math.abs(b) > 1e-12) {
    const d = -(b + Math.sign(b) * Math.sqrt(disc)) / 2;
    candidateTs = [d / a, c / d];
  } else {
    const d = Math.sqrt(disc);
    candidateTs = [(-b + d) / (2 * a), (-b - d) / (2 * a)];
  }
  let intersections = [];
  candidateTs.forEach(t => {
    if (0 <= t && t <= 1) {
      // Intersects the circle
      const isect = (0, _vector.add)((0, _vector.mul)(p1, 1 - t), (0, _vector.mul)(p2, t));
      // Check the angle and compare to the arc

      let angleOfIsect = Math.atan2(isect.y - q.y, isect.x - q.x);
      // Now we can use startAngle and endAngle to know if the arc passes through angleOfIntersection 

      let startAngle = arc.startAngle;
      let endAngle = arc.endAngle;
      (0, _checkAngles.checkAngles)(arc);

      // Ensure startAngle < angleOfIsect < startAngle + 2 PI
      while (angleOfIsect < startAngle) angleOfIsect += Math.PI * 2; // angleOfIsect too small
      while (angleOfIsect > startAngle + Math.PI * 2) angleOfIsect -= Math.PI * 2; // angleOfIsect too big

      if (angleOfIsect <= endAngle) {
        intersections.push({
          point: isect,
          tArc: (angleOfIsect - startAngle) / (endAngle - startAngle),
          tLine: t
        });
      }
    }
  });
  return intersections;
}

/**
 * Finds the intersection points between two arcs
 * See http://paulbourke.net/geometry/circlesphere/
 * @param e1 The first arc
 * @param e2 The second arc
 * @param refine Whether to refine the intersection points using Newton's method
 * @returns An array containing zero, one, or two points of intersection.
 */
function intersectArcArc(e1, e2, refine = false) {
  let d2 = (0, _vector.norm2)((0, _vector.sub)(e1, e2));
  if (d2 > (e1.r + e2.r) * (e1.r + e2.r)) {
    return [];
  }
  if (d2 < (e1.r - e2.r) * (e1.r - e2.r)) {
    return [];
  }
  let d = Math.sqrt(d2);
  const a = (e1.r ** 2 - e2.r ** 2 + d ** 2) / (2 * d);
  const h = Math.sqrt(e1.r ** 2 - a ** 2);
  const e3 = (0, _vector.add)(e1, (0, _vector.mul)((0, _vector.sub)(e2, e1), a / d));
  const intersections = [];
  let candidatePts = [{
    x: e3.x + h * (e2.y - e1.y) / d,
    y: e3.y - h * (e2.x - e1.x) / d
  }, {
    x: e3.x - h * (e2.y - e1.y) / d,
    y: e3.y + h * (e2.x - e1.x) / d
  }];
  if (refine) {
    candidatePts.forEach(isect => {
      let p = {
        ...isect
      };
      let F0, F1;

      // console.log('Refining')

      for (let i = 0; i < 10; i++) {
        F0 = (e1.x - p.x) ** 2 + (e1.y - p.y) ** 2 - e1.r ** 2;
        F1 = (e2.x - p.x) ** 2 + (e2.y - p.y) ** 2 - e2.r ** 2;

        // console.log(p.x, p.y, F0/e0.r**2, F1/e1.r**2)
        if (Math.abs(F0 / e1.r ** 2) + Math.abs(F1 / e2.r ** 2) <= 1e-12) break;
        let J00 = -2 * (e1.x - p.x);
        let J01 = -2 * (e1.y - p.y);
        let J10 = -2 * (e2.x - p.x);
        let J11 = -2 * (e2.y - p.y);

        // TODO: Use Gauss-Jordan elim instead. That will be faster.

        let detJ = J00 * J11 - J01 * J10;
        let invJ00 = J11 / detJ;
        let invJ01 = -J01 / detJ;
        let invJ10 = -J10 / detJ;
        let invJ11 = J00 / detJ;
        let invJtimesF0 = invJ00 * F0 + invJ01 * F1;
        let invJtimesF1 = invJ10 * F0 + invJ11 * F1;

        // console.log(invJtimesF0, invJtimesF1, F0, F1)

        let xnp1 = p.x - invJtimesF0;
        let ynp1 = p.y - invJtimesF1;
        p.x = xnp1;
        p.y = ynp1;
      }
      if (F0 != null && F1 != null && Math.abs(F0 / e1.r ** 2) + Math.abs(F1 / e2.r ** 2) <= 1e-12) {
        isect.x = p.x;
        isect.y = p.y;
      }
    });
  }
  candidatePts.forEach(isect => {
    // Check the angle and compare to the arc

    let arcs = [e1, e2];
    let arcParams = arcs.map(arc => {
      let angleOfIsect = Math.atan2(isect.y - arc.y, isect.x - arc.x);
      // Now we can use startAngle and endAngle to know if the arc passes through angleOfIntersection 

      (0, _checkAngles.checkAngles)(arc);
      let startAngle = arc.startAngle;
      let endAngle = arc.endAngle;

      // Ensure startAngle < angleOfIsect < startAngle + 2 PI
      while (angleOfIsect < startAngle) angleOfIsect += Math.PI * 2; // angleOfIsect too small
      while (angleOfIsect > startAngle + Math.PI * 2) angleOfIsect -= Math.PI * 2; // angleOfIsect too big

      return (angleOfIsect - startAngle) / (endAngle - startAngle);
    });
    if (arcParams[0] <= 1 && arcParams[1] <= 1) {
      intersections.push({
        point: isect,
        t1: arcParams[0],
        t2: arcParams[1]
      });
    }
  });
  return intersections;
}